"use client";

import { useState } from "react";
import { getSiteContent } from "@/content";
import { type Locale, localizedPath } from "@/lib/site";
import { SharedCta } from "@/components/ui/SharedCta";

export function HelpPage({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.help;
  const [query, setQuery] = useState("");

  return (
    <main className="w-full overflow-x-hidden">
      <section className="relative mx-auto w-full max-w-[1280px] overflow-hidden bg-glacier-white px-5 pb-20 pt-[156px] md:px-12 md:pt-[168px]">
        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center text-center">
          <h1 className="typo-page-h1 mb-4 text-primary">{t.hero.title}</h1>
          <p className="typo-section-subtitle mb-10 text-on-surface-variant">{t.hero.copy}</p>
          <div className="mb-6 w-full max-w-3xl">
            <div className="help-search-control group relative">
              <div className="help-search-icon pointer-events-none absolute flex items-center text-secondary">
                <span className="material-symbols-outlined">search</span>
              </div>
              <input className="help-search-input typo-input w-full rounded-[24px] border-2 border-border-pale bg-white shadow-sm transition-all focus:border-ocean-cyan focus:ring-0 group-hover:shadow-md" placeholder={t.hero.placeholder} type="text" value={query} onChange={(event) => setQuery(event.target.value)} />
              <button className="help-search-button absolute flex items-center rounded-2xl bg-parcel-orange px-8 text-white transition-all hover:brightness-110" type="button">
                {t.hero.searchButton}
              </button>
            </div>
          </div>
          <div className="help-hot-search-row">
            <span className="help-hot-search-label typo-status-tag text-text-secondary">{t.hero.hotLabel}</span>
            <div className="help-hot-search-viewport" aria-label={t.hero.hotSearchAria}>
              <div className="help-hot-search-track">
                {[...t.hero.hotTags, ...t.hero.hotTags].map((tag, index) => (
                  <button className="help-hot-search-tag typo-status-tag rounded-full border border-border-pale bg-white px-4 py-1.5 text-secondary transition-colors hover:bg-secondary hover:text-white" type="button" onClick={() => setQuery(tag)} key={`${tag}-${index}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-5 py-16 md:px-12">
        <h2 className="typo-section-h2 mb-10 text-primary">{t.toolsTitle}</h2>
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {t.tools.map(([icon, title, copy, action, path]) => (
            <article className="ui-card group border border-border-pale bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-xl md:p-8" key={title}>
              <div className="ui-icon-tile ui-icon-tile-md mb-6 bg-glacier-white text-secondary transition-colors group-hover:bg-secondary group-hover:text-white">
                <span className="material-symbols-outlined">{icon}</span>
              </div>
              <h3 className="typo-card-title mb-3 text-primary">{title}</h3>
              <p className="typo-card-body mb-6 text-on-surface-variant">{copy}</p>
              <a className="typo-status-tag inline-flex items-center text-secondary transition-colors hover:text-primary" href={localizedPath(locale, path)}>
                {action} <span className="material-symbols-outlined ml-1 text-sm">arrow_forward</span>
              </a>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] bg-glacier-white px-5 py-16 md:px-12">
        <h2 className="typo-section-h2 mb-10 text-primary">{t.categoriesTitle}</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {t.categories.map(([icon, title, copy]) => (
            <article className="ui-card group cursor-pointer border border-border-pale bg-white p-6 transition-all hover:border-secondary" key={title}>
              <div className="mb-4 flex items-center gap-4">
                <span className="material-symbols-outlined text-3xl text-on-tertiary-container">{icon}</span>
                <h3 className="typo-card-title text-primary">{title}</h3>
              </div>
              <p className="typo-card-body mb-4 text-on-surface-variant">{copy}</p>
              <div className="flex justify-end text-secondary transition-transform group-hover:translate-x-1">
                <span className="material-symbols-outlined">chevron_right</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-5 py-16 md:px-12">
        <div className="mb-10 flex items-center justify-between">
          <h2 className="typo-section-h2 text-primary">{t.articlesTitle}</h2>
          <a className="typo-status-tag hidden items-center text-secondary hover:underline md:flex" href={localizedPath(locale, "guide")}>
            {t.articlesMore} <span className="material-symbols-outlined ml-1">arrow_outward</span>
          </a>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {t.articles.map(([tag, title, copy, image]) => (
            <article className="ui-card group flex flex-col overflow-hidden border border-border-pale bg-white transition-shadow hover:shadow-lg" key={title}>
              {image ? (
                <div className="relative h-48 overflow-hidden bg-glacier-white">
                  <img className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" src={image} alt={title || ""} width={1200} height={760} />
                  <div className="typo-status-tag absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-secondary backdrop-blur-md">{tag}</div>
                </div>
              ) : null}
              <div className="p-6">
                {!image ? <div className="typo-status-tag mb-4 w-fit rounded-full bg-glacier-white px-3 py-1 text-secondary">{tag}</div> : null}
                <h3 className="typo-card-title mb-3 line-clamp-2 text-primary">{title}</h3>
                <p className="typo-card-body mb-6 line-clamp-2 text-on-surface-variant">{copy}</p>
                <a className="typo-status-tag flex items-center text-secondary" href={localizedPath(locale, helpArticlePath(tag))}>
                  {t.readFull} <span className="material-symbols-outlined ml-1 text-sm">chevron_right</span>
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="help-first-use-band relative overflow-hidden py-16 text-white">
        <div className="relative z-10 mx-auto w-full max-w-[1280px] px-5 md:px-12">
          <div className="mb-16 text-center">
            <h2 className="typo-section-h2 mb-4 text-white">{t.firstUse.title}</h2>
            <p className="typo-section-subtitle text-glacier-white opacity-80">{t.firstUse.copy}</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {t.firstUse.steps.map(([num, title, copy, action, path, primary]) => (
              <article className="ui-card relative border border-white/10 bg-white/5 p-8 backdrop-blur-sm" key={title as string}>
                <div className="mb-6 text-4xl font-bold text-parcel-orange">{num}</div>
                <h3 className="typo-card-title mb-3">{title}</h3>
                <p className="typo-card-body mb-6 text-white/70">{copy}</p>
                <a className={`typo-button block w-full rounded-2xl py-3 text-center transition-all ${primary ? "bg-parcel-orange text-white hover:brightness-110" : "bg-white text-primary hover:bg-glacier-white"}`} href={localizedPath(locale, path as string)}>
                  {action}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-5 py-16 md:px-12">
        <h2 className="typo-section-h2 mb-10 text-primary">{t.serviceTerms.title}</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {t.serviceTerms.links.map(([icon, title, path]) => (
            <a className="ui-card group flex items-center gap-4 border border-border-pale bg-white p-6 transition-all hover:-translate-y-1 hover:border-secondary hover:shadow-xl" href={localizedPath(locale, path)} key={title}>
              <span className="ui-icon-tile ui-icon-tile-md bg-glacier-white text-secondary transition-colors group-hover:bg-secondary group-hover:text-white">
                <span className="material-symbols-outlined">{icon}</span>
              </span>
              <span className="typo-card-title text-primary group-hover:text-secondary">{title}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="mx-auto mb-16 w-full max-w-[1280px] px-5 md:px-12">
        <SharedCta
          title={t.cta.title}
          copy={t.cta.copy}
          actions={[
            { label: t.cta.actions[0], href: localizedPath(locale, "contact"), icon: "support_agent", variant: "primary" },
            { label: t.cta.actions[1], href: "#top", variant: "secondary" }
          ]}
        />
      </section>
    </main>
  );
}

function helpArticlePath(tag: string) {
  if (tag === "新手指南") return "guide";
  if (tag === "包裹预报") return "tools/parcel-prealert";
  if (tag === "线路选择") return "tools/routes";
  if (tag === "拆箱合包" || tag === "增值服务") return "services/introduction";
  if (tag === "禁运品说明") return "legal/restricted-items";
  return "news";
}
